/**
 * Смарт-кнопки CRM в полосе выбора «Товаров»: типы меню и сборка адресов.
 *
 * Спека — `docs/specs/2026-09-17-products-select-filters-design.md`, раздел 2.
 * Меню — `crmSmartButtons.data.ts`, генерируется из модуля CRM.
 *
 * Номера — как `joinUniq` CRM: повторы убраны, пустые отброшены, запятая без
 * пробелов. Порядок — порядок кольца, тот же, что у «открыть». ПОТОЛОК —
 * `MAX_LIST`: адрес CRM длиннее ~8 КБ получает 414 (замер 17.09.26).
 */

import { MAX_LIST } from "@/lib/productNav";

export type SmartKey =
  | "mpProductID"
  | "modelID"
  | "modelSimilarGroupID"
  | "supplyOrderID"
  | "barcodeQrDataID"
  | "mpStockSupplyPlanDateID"
  | "mpStockSupplyPlanID"
  | "mediaFileID"
  | "cryptoID"
  | "supplySupplierID"
  | "supplyOrderProductPositionID"
  | "serial"
  | "mpReturnID";
export type SmartItem = { id: string; label: string; href: string; confirm?: string };
export type SmartGroup = { label: string; items: SmartNode[] };
export type SmartNode = SmartItem | SmartGroup;
export type SmartMenu = { key: SmartKey; label: string; nodes: SmartNode[] };
/** Номера по ключам; ключ без номеров кнопки не даёт. */
export type SmartIds = Partial<Record<SmartKey, number[]>>;
export type SmartSource = { product_id: number; model_id: number | null; group_id: number | null };

export const CRM_BASE = "https://a.unirenter.ru/b24/";

export function isGroup(node: SmartNode): node is SmartGroup {
  return "items" in node;
}

let меню: Promise<SmartMenu[]> | undefined;

/**
 * Меню CRM — отдельным чанком по требованию (волна F спеки
 * 2026-10-12-code-audit-refactor): 2 490 строк данных нужны там, где стоят
 * смарт-кнопки, а не на входе в кабинет. Обещание ОДНО на вкладку: `use()`
 * узнаёт его по объекту, и новое на каждый рендер держало бы компонент в
 * ожидании вечно. Сорвалось — забываем, чтобы следующий показ попробовал снова.
 */
export function loadCrmMenus(): Promise<SmartMenu[]> {
  меню ??= import("@/lib/crmSmartButtons.data").then(
    (m) => m.CRM_MENUS,
    (e: unknown) => {
      меню = undefined;
      throw e;
    },
  );
  return меню;
}

const ДЕНЬ = new Intl.DateTimeFormat("ru-RU", {
  timeZone: "Europe/Moscow",
  day: "2-digit",
  month: "2-digit",
  year: "2-digit",
});

/** `makeDateRange(-31,-1)` CRM: `дд.мм.гг-дд.мм.гг` за 31…1 день назад, по Москве. */
export function crmRange(today: Date): string {
  const день = (сдвиг: number) => ДЕНЬ.format(new Date(today.getTime() + сдвиг * 86_400_000));
  return `${день(-31)}-${день(-1)}`;
}

export function smartHref(template: string, ids: number[], today: Date): string {
  const d = ДЕНЬ.format(today);
  const адрес = template
    .replaceAll("{ids}", ids.slice(0, MAX_LIST).join(","))
    .replaceAll("{range}", crmRange(today))
    .replaceAll("{today2}", `${d}-${d}`);
  return `${адрес.startsWith("https://") ? "" : CRM_BASE}${адрес}#data`;
}

// Ключи, которые реально считает `smartIds` — своих номеров у остальных ключей
// `SmartKey` («Заказы», «Планы») нет: их отдаёт своя витрина, не отбор товаров.
type КлючТовара = "mpProductID" | "modelID" | "modelSimilarGroupID";

export function smartIds(items: SmartSource[]): SmartIds {
  const out: Record<КлючТовара, number[]> = { mpProductID: [], modelID: [], modelSimilarGroupID: [] };
  const было = { mpProductID: new Set<number>(), modelID: new Set<number>(), modelSimilarGroupID: new Set<number>() };
  const положить = (key: КлючТовара, id: number | null) => {
    if (!id || было[key].has(id)) return;
    было[key].add(id);
    out[key].push(id);
  };
  for (const item of items) {
    положить("mpProductID", item.product_id);
    положить("modelID", item.model_id);
    положить("modelSimilarGroupID", item.group_id);
  }
  return out;
}
